import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import { Link, Radio, RadioGroup, Text } from '@revelio/ui';

import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { PreferenceScope, UserPreferenceKey } from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import layoutStyles from './data-adjustments-editor.module.css';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.DataAdjustments,
  label: 'Data Adjustments',
};

export default function DataAdjustmentsEditor({
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const [adjustment, setAdjustment] = useState<'adjusted' | 'unadjusted'>(() =>
    preferences[UserPreferenceKey.DataAdjustments] ? 'unadjusted' : 'adjusted'
  );

  useEffect(() => {
    setAdjustment(
      preferences[UserPreferenceKey.DataAdjustments] ? 'unadjusted' : 'adjusted'
    );
  }, [preferences]);

  const onUpdate = useCallback(
    (value: 'adjusted' | 'unadjusted') => {
      const isRaw = value === 'unadjusted';
      onPreferenceUpdate?.(UserPreferenceKey.DataAdjustments, isRaw);
      setAdjustment(value);
      // Single GLOBAL choice: always write the GLOBAL record regardless of
      // which page's modal made the change (same pattern as TaxonomiesEditor).
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.DataAdjustments,
        isRaw
      );
    },
    [onPreferenceUpdate]
  );

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Adjustment Type"
        indicateDiff={diff?.includes(meta.id) ?? false}
      />
      <RadioGroup
        value={adjustment}
        onChange={(val) => onUpdate(val as 'adjusted' | 'unadjusted')}
        className={styles.radioGroup}
      >
        <div className={layoutStyles.choiceList}>
          <div className={layoutStyles.choice}>
            <Radio value="adjusted" className={styles.appearanceRadio}>
              Adjusted
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Adjusted data applies sampling weights to correct for
              representation bias (e.g., white-collar workers are
              overrepresented online) and uses a nowcasting model to counteract
              delays in workers updating their profiles after job changes.
            </Text>
          </div>
          <div className={layoutStyles.choice}>
            <Radio value="unadjusted" className={styles.appearanceRadio}>
              Unadjusted
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Unadjusted data shows raw counts without statistical corrections
              for these biases. Learn more about{' '}
              <Link
                href="https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights"
                target="_blank"
                rel="noopener"
                className={styles.link}
              >
                Sampling Weights
              </Link>{' '}
              and{' '}
              <Link
                href="https://www.data-dictionary.reveliolabs.com/methodology.html#lags-in-reporting"
                target="_blank"
                rel="noopener"
                className={styles.link}
              >
                Lags in Reporting
              </Link>
              .
            </Text>
          </div>
        </div>
      </RadioGroup>
    </>
  );
}
